/**
 * orgProfile.service — the org's one-time billing activation (CCR M1).
 *
 * An org must activate a billing profile (pick a region pack + base currency)
 * before ANY document/payment/tax operation works; until then the backend
 * returns 404 ERR_NOT_FOUND "Org billing profile is not activated". The app
 * gates on this (see navigation/activationGate) and shows the setup screen.
 */
import { read, write } from './_base';
import {
  Billing_OrgProfile_Url,
  Billing_OrgProfile_Activate_Url,
  Billing_RegionPacks_Url,
} from '../../api/api.endpoints';

export interface RegionPack {
  code: string;
  name: string;
  version?: number;
}

export interface OrgProfile {
  org_id: string;
  region_pack_code: string;
  base_currency: string;
  gstin: string | null;
  activated_at: string;
}

export interface ActivateProfileInput {
  region_pack_code: string;
  base_currency: string;
  composition_scheme?: boolean;
  gstin?: string;
}

/** GET the org profile. `silent` suppresses the error toast — the gate uses it
 *  to probe (a 404 here is expected for a not-yet-activated org). */
export const getOrgProfile = (silent = false) =>
  read<OrgProfile>(Billing_OrgProfile_Url, 'Failed to load billing profile', undefined, silent);

export const listRegionPacks = () =>
  read<{ region_packs: RegionPack[] }>(Billing_RegionPacks_Url, 'Failed to load regions').then(
    (r) => r.region_packs ?? [],
  );

export const activateOrgProfile = (body: ActivateProfileInput) =>
  write<OrgProfile>('post', Billing_OrgProfile_Activate_Url, {
    body,
    errorTitle: 'Could not set up billing',
    successTitle: 'Billing is ready',
    successFallback: 'Your account is set up — you can now create documents.',
  });
